<template>
  <AIVDialog
    v-model="isVisible"
    :title="strategy && strategy.id ? 'Edit Support Strategy' : 'New Support Strategy'"
    width="700px"
    height="80vh"
    class="support-strategy-modal"
  >
    <div v-if="loading" class="loading-container">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <div v-else>
      <!-- Plan Cycle Date -->
      <div class="form-section">
        <label class="form-label">Plan Cycle Date</label>
        <el-date-picker
          v-model="form.planCycleDate"
          type="date"
          placeholder="Plan cycle date"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          style="width: 100%"
        />
      </div>

      <!-- Service Codes -->
      <div class="form-section">
        <label class="form-label">Service Code(s)</label>
        <AIVSelect
          v-model="form.serviceCodes"
          :options="serviceCodeOptions"
          placeholder="Select Service Codes"
          :searchable="false"
          :clearable="true"
          :multiSelect="true"
          style="width: 100%"
        />
      </div>

      <!-- Goals -->
      <div class="form-section">
        <label class="form-label">This Year's Goal(s)</label>
        <div v-for="(goal, index) in form.goals" :key="'goal-' + index" class="goal-item">
          <div class="goal-item-header">
            <span class="list-item-number">#{{ index + 1 }}</span>
            <div class="goal-type-toggle">
              <el-button
                :type="goal.type === 'free_response' ? 'primary' : 'default'"
                size="mini"
                @click="goal.type = 'free_response'"
              >
                Free Response
              </el-button>
              <el-button
                :type="goal.type === 'yes_no' ? 'primary' : 'default'"
                size="mini"
                @click="goal.type = 'yes_no'"
              >
                Yes/No
              </el-button>
            </div>
            <el-button
              type="danger"
              icon="fa fa-times"
              size="mini"
              circle
              @click="form.goals.splice(index, 1)"
            ></el-button>
          </div>
          <el-input
            v-model="goal.text"
            :placeholder="goal.type === 'yes_no' ? 'Enter a yes/no goal...' : 'Enter goal...'"
            size="small"
            style="margin-top: 4px"
          />
        </div>
        <el-button size="small" @click="addGoal" style="margin-top: 4px">
          <i class="fa fa-plus" style="margin-right: 4px"></i> Add Goal
        </el-button>
      </div>

      <!-- Desired Outcomes -->
      <div class="form-section">
        <label class="form-label">Desired Outcome Support Item(s)</label>
        <div v-for="(outcome, index) in form.desiredOutcomes" :key="'outcome-' + index" class="list-item-row">
          <span class="list-item-number">#{{ index + 1 }}</span>
          <el-input
            v-model="form.desiredOutcomes[index]"
            type="textarea"
            :rows="2"
            placeholder="List support to the goal and the codes assigned..."
          />
          <el-button
            type="danger"
            icon="fa fa-times"
            size="mini"
            circle
            @click="form.desiredOutcomes.splice(index, 1)"
          ></el-button>
        </div>
        <el-button size="small" @click="form.desiredOutcomes.push('')" style="margin-top: 4px">
          <i class="fa fa-plus" style="margin-right: 4px"></i> Add Outcome
        </el-button>
      </div>

      <!-- Support Details Specific to Individual -->
      <div class="form-section">
        <label class="form-label">Support Details Specific to the Individual</label>
        <el-input
          v-model="form.supportDetailsIndividual"
          type="textarea"
          :rows="3"
          placeholder="Details specific to this individual..."
        />
      </div>

      <!-- Strategies Implemented -->
      <div class="form-section">
        <label class="form-label">Strategies Implemented</label>
        <el-input
          v-model="form.strategiesImplemented"
          type="textarea"
          :rows="3"
          placeholder="Describe strategies implemented..."
        />
      </div>
    </div>

    <template slot="footer">
      <el-button @click="isVisible = false">Cancel</el-button>
      <el-button type="primary" @click="saveDraft" :loading="saving">
        <i class="fa fa-save" style="margin-right: 4px"></i> Save Draft
      </el-button>
      <el-button type="success" @click="generateAndSave" :loading="generating">
        <i class="fa fa-file-pdf" style="margin-right: 4px"></i> Generate PDF & Save
      </el-button>
    </template>
  </AIVDialog>
</template>

<script>
import { supportStrategiesService } from "src/serviceHandlers/supportStrategiesService";

export default {
  name: "SupportStrategyModal",
  props: {
    value: { type: Boolean, default: false },
    clientId: { type: [Number, String], required: true },
    strategyId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      generating: false,
      strategy: null,
      availableServiceCodes: [],
      form: {
        planCycleDate: null,
        serviceCodes: [],
        goals: [{ text: "", type: "free_response" }],
        desiredOutcomes: [""],
        supportDetailsIndividual: "",
        strategiesImplemented: "",
      },
    };
  },
  computed: {
    isVisible: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
    serviceCodeOptions() {
      return this.availableServiceCodes.map((sc) => ({
        name: `${sc.code} - ${sc.name}`,
        shortName: sc.code,
        id: sc.code,
      }));
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.init();
      }
    },
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        await this.loadServiceCodes();

        if (this.strategyId) {
          const data = await supportStrategiesService.getSupportStrategy(this.strategyId);
          this.strategy = data;
          this.form.planCycleDate = data.plan_cycle_date;
          try { this.form.serviceCodes = JSON.parse(data.service_codes || "[]"); } catch { this.form.serviceCodes = []; }
          try {
            const parsed = JSON.parse(data.goals || "[]");
            this.form.goals = parsed.map(g =>
              typeof g === "string" ? { text: g, type: "free_response" } : g
            );
          } catch { this.form.goals = []; }
          try { this.form.desiredOutcomes = JSON.parse(data.desired_outcomes || "[]"); } catch { this.form.desiredOutcomes = []; }
          this.form.supportDetailsIndividual = data.support_details_individual || "";
          this.form.strategiesImplemented = data.strategies_implemented || "";
          if (this.form.goals.length === 0) this.form.goals = [{ text: "", type: "free_response" }];
          if (this.form.desiredOutcomes.length === 0) this.form.desiredOutcomes = [""];
        } else {
          this.strategy = null;
          this.form = {
            planCycleDate: null,
            serviceCodes: [],
            goals: [{ text: "", type: "free_response" }],
            desiredOutcomes: [""],
            supportDetailsIndividual: "",
            strategiesImplemented: "",
          };
        }
      } catch (err) {
        this.$notifyError("Failed to load data");
      } finally {
        this.loading = false;
      }
    },

    async loadServiceCodes() {
      try {
        this.availableServiceCodes = await supportStrategiesService.getClientServiceCodes(
          this.clientId,
        ) || [];
      } catch {
        this.availableServiceCodes = [];
      }
    },

    addGoal() {
      this.form.goals.push({ text: "", type: "free_response" });
    },

    getPayload() {
      return {
        clientId: parseInt(this.clientId),
        planCycleDate: this.form.planCycleDate,
        serviceCodes: this.form.serviceCodes,
        goals: this.form.goals.filter((g) => g.text.trim()),
        desiredOutcomes: this.form.desiredOutcomes.filter((o) => o.trim()),
        supportDetailsIndividual: this.form.supportDetailsIndividual,
        strategiesImplemented: this.form.strategiesImplemented,
      };
    },

    getUpdatePayload() {
      return {
        plan_cycle_date: this.form.planCycleDate,
        service_codes: JSON.stringify(this.form.serviceCodes),
        goals: JSON.stringify(this.form.goals.filter((g) => g.text.trim())),
        desired_outcomes: JSON.stringify(this.form.desiredOutcomes.filter((o) => o.trim())),
        support_details_individual: this.form.supportDetailsIndividual,
        strategies_implemented: this.form.strategiesImplemented,
        status: "draft",
      };
    },

    async saveDraft() {
      this.saving = true;
      try {
        if (this.strategy && this.strategy.id) {
          this.strategy = await supportStrategiesService.updateSupportStrategy(
            this.strategy.id,
            this.getUpdatePayload(),
          );
        } else {
          this.strategy = await supportStrategiesService.createSupportStrategy(this.getPayload());
        }
        this.$notifySuccess("Support strategy saved as draft");
        this.$emit("saved", this.strategy);
      } catch (err) {
        this.$notifyError("Failed to save support strategy");
      } finally {
        this.saving = false;
      }
    },

    async generateAndSave() {
      this.generating = true;
      try {
        // Create or update the strategy first
        if (this.strategy && this.strategy.id) {
          this.strategy = await supportStrategiesService.updateSupportStrategy(
            this.strategy.id,
            this.getUpdatePayload(),
          );
        } else {
          this.strategy = await supportStrategiesService.createSupportStrategy(this.getPayload());
        }

        // Generate the PDF and save as document
        const result = await supportStrategiesService.generateDocument(this.strategy.id);

        this.$notifySuccess("Support Strategy PDF generated and saved");
        this.$emit("document-generated", result.document);
        this.$emit("input", false);
      } catch (err) {
        this.$notifyError("Failed to generate document: " + (err.message || "Unknown error"));
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style scoped>
.support-strategy-modal :deep(.el-dialog__body) {
  height: calc(80vh - 120px) !important;
  overflow-y: auto !important;
  padding: 20px !important;
}

.loading-container {
  text-align: center;
  padding: 30px;
  color: #909399;
}

.form-section {
  margin-bottom: 12px;
}

.form-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  color: #e0e0e0;
}

.list-item-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}

.list-item-number {
  color: #909399;
  font-weight: 600;
  font-size: 13px;
  min-width: 24px;
  padding-top: 6px;
}

.goal-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
}

.goal-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.goal-type-toggle {
  flex: 1;
}

.goal-type-toggle .el-button {
  padding: 4px 10px;
  font-size: 12px;
}
</style>
